import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { CRMAuthProvider, useCRMAuth } from './context/CRMAuthContext';
import CRMLogin from './pages/crm/CRMLogin';
import CRMRequests from './pages/crm/CRMRequests';
import CRMArticles from './pages/crm/CRMArticles';
import CRMDashboard from './pages/crm/CRMDashboard';
import CRMStaffManagement from './pages/crm/CRMStaffManagement';
import CRMRatings from './pages/crm/CRMRatings';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import SupportGroupPopup from './components/common/SupportGroupPopup';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ClientDashboard from './pages/ClientDashboard';
import AttorneyDashboard from './pages/AttorneyDashboard';
import FindAttorney from './pages/FindAttorney';
import AttorneyProfile from './pages/AttorneyProfile';
import RequestAdvice from './pages/RequestAdvice';
import PayQR from './pages/PayQR';
import Subscription from './pages/Subscription';
import LegalTopics from './pages/LegalTopics';
import ClientConsultations from './pages/ClientConsultations';
import AttorneyRequests from './pages/AttorneyRequests';
import ArticleEditor from './pages/ArticleEditor';
import QueryResults from './pages/QueryResults';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RatingMethodology from './pages/RatingMethodology';
import DeleteAccountRequest from './pages/DeleteAccountRequest';
import ContactAttorney from './pages/ContactAttorney';
import ClientMessages from './pages/ClientMessages';
import AttorneyMessages from './pages/AttorneyMessages';
import SavedAttorneys from './pages/SavedAttorneys';
import RatingSubmission from './pages/RatingSubmission';
import { useEffect } from 'react';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', color: 'red' }}>
          <h1>Something went wrong.</h1>
          <pre>{this.state.error?.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_ENABLED = GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'placeholder-for-google-oauth';

const styles = {
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#4a5568',
  },
};

const ProtectedRoute = ({ children, allowedUserType }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedUserType && user.userType !== allowedUserType) {
    return <Navigate to="/" />;
  }

  return children;
};

const CRMProtectedRoute = ({ children, role }) => {
  const { crmUser, loading } = useCRMAuth();
  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!crmUser) return <Navigate to="/crm" replace />;
  if (role && crmUser.role !== role) return <Navigate to="/crm/requests" replace />;
  return children;
};

const AppContent = () => {
  const { loading } = useAuth();
  const location = useLocation();
  const isCRM = location.pathname.startsWith('/crm');

  if (loading && !isCRM) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (isCRM) {
    return (
      <Routes>
        <Route path="/crm" element={<CRMLogin />} />
        <Route path="/crm/requests" element={<CRMProtectedRoute><CRMRequests /></CRMProtectedRoute>} />
        <Route path="/crm/articles" element={<CRMProtectedRoute role="admin"><CRMArticles /></CRMProtectedRoute>} />
        <Route path="/crm/dashboard" element={<CRMProtectedRoute role="admin"><CRMDashboard /></CRMProtectedRoute>} />
        <Route path="/crm/staff" element={<CRMProtectedRoute role="admin"><CRMStaffManagement /></CRMProtectedRoute>} />
        <Route path="/crm/rating-submissions" element={<CRMProtectedRoute role="admin"><CRMRatings /></CRMProtectedRoute>} />
      </Routes>
    );
  }

  return (
    <div style={styles.app}>
      <ScrollToTop />
      <SupportGroupPopup />
      <Navbar />
      <main style={styles.main}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/find-attorney" element={<FindAttorney />} />
          <Route path="/attorney/dashboard" element={
            <ProtectedRoute allowedUserType="attorney">
              <AttorneyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/attorney/:id" element={<AttorneyProfile />} />
          <Route path="/legal-topics" element={<LegalTopics />} />
          <Route path="/legal-topics/:slug" element={<LegalTopics />} />
          <Route path="/query-results" element={<QueryResults />} />
          {/* Client Routes */}
          <Route path="/client/dashboard" element={
            <ProtectedRoute allowedUserType="client">
              <Navigate to="/client/consultations" />
            </ProtectedRoute>
          } />
          <Route path="/client/consultations" element={
            <ProtectedRoute allowedUserType="client">
              <ClientConsultations />
            </ProtectedRoute>
          } />
          <Route path="/request-advice" element={<RequestAdvice />} />
          <Route path="/pay-qr" element={<PayQR />} />
          <Route path="/contact/:id" element={<ContactAttorney />} />
          <Route path="/client/messages" element={
            <ProtectedRoute allowedUserType="client">
              <ClientMessages />
            </ProtectedRoute>
          } />
          <Route path="/client/saved-attorneys" element={
            <ProtectedRoute allowedUserType="client">
              <SavedAttorneys />
            </ProtectedRoute>
          } />

          {/* Attorney Routes */}
          <Route path="/attorney/requests" element={
            <ProtectedRoute allowedUserType="attorney">
              <AttorneyRequests />
            </ProtectedRoute>
          } />
          <Route path="/attorney/messages" element={
            <ProtectedRoute allowedUserType="attorney">
              <AttorneyMessages />
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute allowedUserType="attorney">
              <Subscription />
            </ProtectedRoute>
          } />
          <Route path="/attorney/articles/new" element={
            <ProtectedRoute allowedUserType="attorney">
              <ArticleEditor />
            </ProtectedRoute>
          } />
          <Route path="/attorney/articles/:id/edit" element={
            <ProtectedRoute allowedUserType="attorney">
              <ArticleEditor />
            </ProtectedRoute>
          } />
          <Route path="/attorney/rating-submission" element={
            <ProtectedRoute allowedUserType="attorney">
              <RatingSubmission />
            </ProtectedRoute>
          } />

          {/* Catch-all */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/rating-methodology" element={<RatingMethodology />} />
          <Route path="/delete-account" element={<DeleteAccountRequest />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const AppWithProviders = () => {
  const content = (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <CRMAuthProvider>
            <Router>
              <AppContent />
            </Router>
          </CRMAuthProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );

  if (GOOGLE_ENABLED) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {content}
      </GoogleOAuthProvider>
    );
  }

  return content;
};

export default AppWithProviders;
